import { setMachineDetailsData } from '../../redux/machineDetailsDataSlice';
import { SuccessfullAddArticleItemDialog } from '../articleFolder/oneArticleFolderDetails/articles/successfullAddArticleItemDialog';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useHasPermission } from '../../hooks/useHasPermission';
import { EditArticleItemDialog } from './editMachineContentDialog';
import { AddArticleItemDialog } from './addMachineContentDialog';
import useResponsive from '../../hooks/useResponsive';
import { useSelector, useDispatch } from 'react-redux';
import { useArticleMediaViewer } from '../articleFolder/useArticleMediaViewer';
import { DeleteRecord } from '../deleteRecord';
import RichTextDisplay from '../richText/RichTextDisplay';
import { Box, Typography, Button, ButtonGroup } from '@mui/material';
import NoRecord from '../NoRecord';
import React, { useMemo, useState } from 'react';
import Iconify from '../Iconify';
import axios from 'axios';
import '../../css/machines/MachineFiltration.css';
import {
  DEFAULT_STATUS_FILTER,
  PUBLICATION_STATUS_LABELS,
} from '../../utils/articleItemMediaUtils';
import { ArticlePublicationStatusChip } from '../articleFolder/ArticleMetaChips';
import ContentMediaGallery from '../contentMedia/ContentMediaGallery';
import { getContentGalleryItems } from '../../utils/contentMediaUtils';
import { toContentItemShape } from './machineContentApi';
import OrderMoveButtons from '../listOrder/OrderMoveButtons';
import { swapListItem } from '../../utils/listOrder';

const ALL_PUBLICATION_STATUSES = Object.keys(PUBLICATION_STATUS_LABELS);

const isAllStatusesSelected = (filter) =>
  ALL_PUBLICATION_STATUSES.every((status) => filter.includes(status));

const ArticleItems = () => {

    const dispatch = useDispatch();
    const isDesktop = useResponsive('up', 'lg');
    const canView = useHasPermission('machine_view');
    const canUpdate = useHasPermission('machine_update');
    const machineDetailsData = useSelector((state) => state.MachineDetailsData);
    const { openArticleMedia, mediaViewer } = useArticleMediaViewer();

    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
    const [openAddArticleItem, setOpenAddArticleItem] = useState(false);
    const [articleToDelete, setArticleToDelete] = useState(null);
    const [openDeleteArticleItem, setOpenDeleteArticleItem] = useState(false);
    const [editArticleItem, setEditArticleItem] = useState({ data: null, open: false });
    const [openSuccessfulAddArticleItem, setOpenSuccessfulAddArticleItem] = useState(false);

    const contents = useMemo(
        () => (machineDetailsData.machine_content || []).map(toContentItemShape),
        [machineDetailsData.machine_content],
    );

    const filteredArticles = useMemo(() => (
        contents.filter((item) => statusFilter.includes(item.publication_status))
    ), [contents, statusFilter]);

    const moveContent = async (id, direction) => {
        const items = machineDetailsData.machine_content || [];
        try {
            const response = await axios.patch(`machine-content/${id}/move`, { direction });
            if (response.data?.moved) {
                dispatch(setMachineDetailsData({
                    ...machineDetailsData,
                    machine_content: swapListItem(items, id, direction),
                }));
            }
        } catch (error) {
            CustomErrorLogger(error, 'Move machine content');
        }
    };

    const openDeleteArticleDialog = (id) => {
        setArticleToDelete(id);
        setOpenDeleteArticleItem(true);
    };

    const handleDeleteArticle = async () => {
        try {
            const response = await axios.delete(`machine-content/${articleToDelete}`);
            if (response.status === 200 || response.status === 201) {
                setOpenDeleteArticleItem(false);
                dispatch(setMachineDetailsData({
                    ...machineDetailsData,
                    machine_content: machineDetailsData.machine_content.filter((item) => item.id !== articleToDelete),
                }));
            }
        } catch (error) {
            console.error('Error deleting machine_content:', error);
            CustomErrorLogger(error, "Delete article");
        }
    };

    const handleSelectAllStatuses = () => {
        setStatusFilter((prev) => (
            isAllStatusesSelected(prev) ? [...DEFAULT_STATUS_FILTER] : [...ALL_PUBLICATION_STATUSES]
        ));
    };

    const handleToggleStatus = (status) => {
        setStatusFilter((prev) => {
            if (prev.includes(status)) {
                const next = prev.filter((value) => value !== status);
                return next.length > 0 ? next : prev;
            }
            return [...prev, status];
        });
    };

    const getStatusButtonClass = (status) => (
        statusFilter.includes(status)
            ? 'MachineFiltration_options_selected_button'
            : 'MachineFiltration_options_button'
    );

    return (
        canView ? (
        <Box sx={{ backgroundColor: '#FFFFFF', margin: '30px 0px 10px 0px', borderRadius: '8px', padding: '20px' }}>
            <Box className='modeEtConsigne_edit_box' sx={{ marginBottom: '20px' }}>
                <Typography className='modeEtConsigne_title'>Contenu pédagogique</Typography>
                {isDesktop && canUpdate && contents.length > 0 &&
                    <Box className='role_page_header_action_box' onClick={() => setOpenAddArticleItem(true)}>
                        <Iconify className='role_page_header_add_icon' icon={"ic:baseline-plus"} />
                        <Typography className='role_page_header_action_title'>Ajouter un contenu</Typography>
                    </Box>
                }
                {!isDesktop &&
                    <Box className='role_page_header_action_box'>
                        {canUpdate && <Iconify className='role_page_header_add_icon' icon={"ic:baseline-plus"} onClick={() => setOpenAddArticleItem(true)} />}
                    </Box>
                }
            </Box>

            {contents.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <ButtonGroup variant="outlined">
                        <Button
                            className={isAllStatusesSelected(statusFilter)
                                ? 'MachineFiltration_options_selected_button'
                                : 'MachineFiltration_options_button'}
                            onClick={handleSelectAllStatuses}
                        >
                            Tout
                        </Button>
                        {Object.entries(PUBLICATION_STATUS_LABELS).map(([value, label]) => (
                            <Button
                                key={value}
                                className={getStatusButtonClass(value)}
                                onClick={() => handleToggleStatus(value)}
                            >
                                {label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>
            )}

            {contents.length === 0 ?
                <NoRecord
                    description="Aucun contenu pédagogique pour le moment"
                    buttonTitle="Ajouter un contenu"
                    handleOpenDialog={() => setOpenAddArticleItem(true)}
                    hasPermission={canUpdate}
                />
                : filteredArticles.length === 0 ?
                <NoRecord description="Aucun contenu pour les statuts sélectionnés" />
                :
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {filteredArticles.map((item) => {
                        const source = machineDetailsData.machine_content || [];
                        const fullIndex = source.findIndex((row) => row.id === item.id);
                        return (
                        <Box
                            key={item.id}
                            sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', backgroundColor: '#F6F6F6', borderRadius: '8px', padding: '10px' }}
                        >
                            <Box className='modeEtConsigne_edit_box'>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography className='modeEtConsigne_title'>{item.title}</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        <ArticlePublicationStatusChip status={item.publication_status} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {canUpdate && (
                                        <OrderMoveButtons
                                            disableUp={fullIndex <= 0}
                                            disableDown={fullIndex < 0 || fullIndex >= source.length - 1}
                                            onMove={(direction) => moveContent(item.id, direction)}
                                        />
                                    )}
                                    {canUpdate && <Iconify className='modeEtConsigne_edit_icon' sx={{ color: '#F12525 !important' }} icon={"ic:outline-delete"} onClick={() => openDeleteArticleDialog(item.id)} />}
                                    {canUpdate && <Iconify className='modeEtConsigne_edit_icon' icon={"ic:outline-edit"} onClick={() => setEditArticleItem({ data: item, open: true })} />}
                                </Box>
                            </Box>
                            <RichTextDisplay html={item.description} />
                            {canView && getContentGalleryItems(item).length > 0 && (
                                <ContentMediaGallery
                                    readOnly
                                    items={getContentGalleryItems(item)}
                                    onOpen={(index) => openArticleMedia(item, index)}
                                />
                            )}
                        </Box>
                    );
                    })}
                </Box>
            }

            {openAddArticleItem &&
                <AddArticleItemDialog
                    openAddArticleItem={openAddArticleItem}
                    setOpenAddArticleItem={setOpenAddArticleItem}
                    setOpenSuccessfulAddArticleItem={setOpenSuccessfulAddArticleItem}
                />
            }

            {openSuccessfulAddArticleItem &&
                <SuccessfullAddArticleItemDialog
                    openSuccessfulAddArticleItem={openSuccessfulAddArticleItem}
                    setOpenSuccessfulAddArticleItem={setOpenSuccessfulAddArticleItem}
                    setOpenAddArticleItem={setOpenAddArticleItem}
                    listLabel="Revenir à la fiche machine"
                    addAnotherLabel="Ajouter un autre contenu"
                />
            }

            {editArticleItem.open &&
                <EditArticleItemDialog
                    editArticleItem={editArticleItem}
                    setEditArticleItem={setEditArticleItem}
                />
            }

            {openDeleteArticleItem &&
                <DeleteRecord
                    openDelete={openDeleteArticleItem}
                    setOpenDelete={setOpenDeleteArticleItem}
                    handleDelete={handleDeleteArticle}
                    title="Êtes-vous sûr de vouloir supprimer ce contenu pédagogique ?"
                />
            }

            {mediaViewer}

        </Box>
        ) : null
    );
};

export default ArticleItems;

